import {
  Datagrid,
  Filter,
  FunctionField,
  NumberField,
  ResourceContextProvider,
  TextField,
  Record,
  Link,
} from "react-admin";
import { parseISO } from "date-fns";
import { GenericDateOrTimePicker } from "../../common/form/GenericDateOrTimePicker";
import { DatePicker } from "@material-ui/pickers";
import resourceNames from "../../../common/data/resourceNames";
import { useEffect, useMemo, useState } from "react";
import { DefaultProLogsList } from "../../common/list/DefaultProLogsList";
import { ProLogsEmpty } from "../../common/list/ProLogsEmpty";
import { HistoryOutlined, Visibility } from "@material-ui/icons";
import {
  TripsViewListDto,
  VehicleViewApiFactory,
  VehicleViewDto,
} from "../../../common/data/service-proxies";
import { VehicleBreadcrumb } from "../../assets/maintenance-dashboard/VehicleBreadcrumb";
import { Breadcrumbs, Typography } from "@material-ui/core";
import { useAxiosInstance } from "../../../common/hooks/useAxiosInstance";
import { handleAxiosError } from "../../../common/data/axiosErrorHandler";
import { StateBreadcrumb } from "./StateBreadcrumb";
import { ActionsField } from "../../common/data-grid-fields/ActionsField";
import { ShowLocationHistoryMenuItem } from "./ShowLocationHistoryMenuItem";
import { useLocation } from "react-router-dom";

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

interface BreadcrumbProps {
  item?: VehicleViewDto | string;
  type?: string;
  dateFrom?: string | null;
  dateTo?: string | null;
}

export const Breadcrumb = ({
  item,
  type,
  dateFrom,
  dateTo,
}: BreadcrumbProps) => {
  const query =
    dateFrom && dateTo ? `?dateFrom=${dateFrom}&dateTo=${dateTo}` : "";
  return (
    <Breadcrumbs aria-label="breadcrumb" className="vehicle-breadcrumbs">
      <Link to={`/${resourceNames.LOCATIONS_STATE_TRIPS}`}>
        <Typography variant="h6">State Trips</Typography>
      </Link>
      {type === "vehicle" && (
        <VehicleBreadcrumb
          vehicle={item as VehicleViewDto}
          getBasePath={(x) =>
            x
              ? `/${resourceNames.LOCATIONS_STATE_TRIPS}/vehicle/${x.id}${query}`
              : `/${resourceNames.LOCATIONS_STATE_TRIPS}`
          }
        />
      )}
      {type === "state" && (
        <StateBreadcrumb
          state={{ id: item as number }}
          getBasePath={(x) =>
            x
              ? `/${resourceNames.LOCATIONS_STATE_TRIPS}/state/${x.id}${query}`
              : `/${resourceNames.LOCATIONS_STATE_TRIPS}`
          }
        />
      )}
    </Breadcrumbs>
  );
};

const ListFilter = (props: any) => {
  return (
    <Filter {...props}>
      <GenericDateOrTimePicker
        PickerComponent={DatePicker}
        className="list-view-filter"
        label="Date from"
        source="dateFrom"
        alwaysOn
        options={{
          minDate: new Date("2024-01-01T06:00:00Z"),
          maxDate: new Date(),
        }}
      />
      <GenericDateOrTimePicker
        PickerComponent={DatePicker}
        className="list-view-filter"
        label="Date to"
        source="dateTo"
        alwaysOn
        options={{
          minDate: new Date("2024-01-01T06:00:00Z"),
          maxDate: new Date(),
        }}
      />
    </Filter>
  );
};

export const StateTripsSingle = (props: any) => {
  const query = useQuery();

  const axios = useAxiosInstance();
  const [vehicle, setVehicle] = useState<VehicleViewDto>();

  const filters = useMemo(() => {
    const filters: { vehicleId?: string; stateId?: string } = {};

    if (props.type === "vehicle") {
      filters.vehicleId = props.id;
    }

    if (props.type === "state") {
      filters.stateId = props.id;
    }

    return filters;
  }, [props.id, props.type]);

  useEffect(() => {
    if (!props.id && props.type !== "vehicle") {
      return;
    }

    if (!axios) {
      return;
    }

    VehicleViewApiFactory(undefined, axios.defaults.baseURL, axios)
      .apiAppVehicleviewIdGet(props.id)
      .then((response) => setVehicle(response.data))
      .catch((e) => console.error(handleAxiosError(e)));
  }, [props.id, props.type, axios]);

  return (
    <div className="content-container">
      <ResourceContextProvider value={resourceNames.LOCATIONS_STATE_TRIPS}>
        <DefaultProLogsList
          empty={<ProLogsEmpty iconType={HistoryOutlined} />}
          className="content-list"
          title={
            <Breadcrumb
              type={props.type}
              item={props.type === "vehicle" ? vehicle : props.id}
              dateFrom={query.get("dateFrom")}
              dateTo={query.get("dateTo")}
            />
          }
          bulkActionButtons={false}
          filters={<ListFilter />}
          sort={{
            field: "date",
            order: "ASC",
          }}
          filterDefaultValues={{
            dateFrom: query.get("dateFrom"),
            dateTo: query.get("dateTo"),
          }}
          filter={{
            ...filters,
          }}
          {...props}
        >
          <Datagrid>
            {props.type === "state" && (
              <TextField source="vehicleName" label="Vehicle" />
            )}
            {props.type === "vehicle" && (
              <TextField source="stateCode" label="State" />
            )}
            <FunctionField<TripsViewListDto & Record>
              source="date"
              label="Date"
              render={(x) =>
                parseISO(x!.date!).toLocaleDateString("en-US", {
                  timeZone: x?.ianaTerminalTimeZone ?? undefined,
                })
              }
            />
            <NumberField source="startOdometer" label="Start odometer" />
            <NumberField source="endOdometer" label="End odometer" />
            <NumberField source="distance" label="Distance" />
            <ActionsField>
              <ShowLocationHistoryMenuItem
                label="View location history"
                icon={<Visibility />}
              />
            </ActionsField>
          </Datagrid>
        </DefaultProLogsList>
      </ResourceContextProvider>
    </div>
  );
};
